import {
  EndOfLife,
  EOLStatusEnum,
  EOLStatus as EOLStatusType,
} from '@/types/devices';
import { differenceInMonths, isBefore, parseISO } from 'date-fns';
import semver, { SemVer } from 'semver';
import { OsType } from '@/components/DeviceOverview/types';
import { Asset } from '@/types/assets';
import { VersionCounts } from './deviceOverviewHelpers';

export const EOL_WARN_THRESHOLD_MONTHS = 3 as const;

export type EOLProduct = OsType;

const WindowsEditionEnum = {
  CORE: 'CORE',
  PROFESSIONAL: 'PROFESSIONAL',
  ENTERPRISE: 'ENTERPRISE',
} as const;

const WindowsEditionValues = Object.values(WindowsEditionEnum);

export interface DeviceLike {
  osType: OsType;
  osVersion?: string | null; // e.g. "14.4.1" (macOS) or "10.0.19045" (Windows)
  editionId?: string | null; // Windows only
}

/**
 * Check if the given device is EOL
 * @param device - the device to check
 * @param allEols - all EOLs for the device
 * @returns the EOL for the given device, or undefined if no EOL is found
 */
export function checkDeviceEol(
  device: DeviceLike,
  allEols: EndOfLife[]
): EndOfLife | undefined {
  if (!device?.osVersion) return undefined;

  if (device.osType === OsType.MACOS) {
    return checkMacOSEol(device.osVersion, allEols);
  }

  if (device.osType === OsType.WINDOWS) {
    const edition =
      WindowsEditionValues.find(value => value === device.editionId) ?? null;
    return checkWindowsEol(device.osVersion, edition, allEols);
  }

  return undefined;
}

/**
 * Check if the given OS version is EOL for macOS
 * @param osVersion - e.g. "14.4.1"
 * @param allEols - all EOLs for macOS
 * @returns the EOL for the given OS version, or undefined if no EOL is found
 */
function checkMacOSEol(
  osVersion: string,
  allEols: EndOfLife[]
): EndOfLife | undefined {
  const parsed = safeParseSemver(osVersion);
  if (!parsed) return undefined;

  // macOS cycle: major for 11+, and "10.minor" for 10.x
  let cycle = String(parsed.major);
  if (parsed.major === 10) cycle += `.${parsed.minor}`;

  const eol = getMacOSEolByCycle(cycle, allEols);

  return eol ?? undefined;
}

/**
 * Get the EOL for the given cycle
 * @param cycle - e.g. "11" or "10.15"
 * @param allEols - all EOLs for macOS
 * @returns the EOL for the given cycle, or undefined if no EOL is found
 */
function getMacOSEolByCycle(
  cycle: string,
  allEols: EndOfLife[]
): EndOfLife | undefined {
  return allEols.find(e => e.product === OsType.MACOS && e.version === cycle);
}

/**
 * Check if the given OS version is EOL for Windows
 * @param osVersion - e.g. "10.0.19045"
 * @param edition - e.g. "CORE" or "PROFESSIONAL" or "ENTERPRISE"
 * @param allEols - all EOLs for Windows
 * @returns the EOL for the given OS version, or undefined if no EOL is found
 */
function checkWindowsEol(
  osVersion: string,
  edition: (typeof WindowsEditionValues)[number] | null,
  allEols: EndOfLife[]
): EndOfLife | undefined {
  // Version like "10.0.19045" → take build number "19045"
  const parts = osVersion.split('.');
  const build = parts[parts.length - 1];
  if (!build) return undefined;

  const windowsEols = allEols.filter(e => e.product === OsType.WINDOWS);

  // Filter those whose `latest` ends with the build number (matches your backend logic)
  const candidates = windowsEols.filter(
    e => e.latest && e.latest.endsWith(build)
  );
  if (candidates.length === 0) return undefined;

  const eol = chooseWindowsEolByEdition(candidates, edition);

  return eol ?? undefined;
}

/**
 * If multiple candidates, pick by edition letter:
 *  - CORE/PROFESSIONAL → 'w'
 *  - ENTERPRISE        → 'e'
 * Fallback when edition is null/undefined:
 *   1. Generic cycle (no edition suffix) if available
 *   2. Otherwise earliest EOL date
 * Expected `version` layout: "10-21h2-w" | "10-21h2-e" | generic "10-21h2"
 */
function chooseWindowsEolByEdition(
  candidates: EndOfLife[],
  edition: (typeof WindowsEditionValues)[number] | null | undefined
): EndOfLife | undefined {
  let letter: string | undefined;

  if (edition === WindowsEditionEnum.ENTERPRISE) {
    letter = 'e';
  } else if (
    edition === WindowsEditionEnum.CORE ||
    edition === WindowsEditionEnum.PROFESSIONAL
  ) {
    letter = 'w';
  }

  let generic: EndOfLife | undefined;

  for (const e of candidates) {
    const parts = e.version.split('-'); // ["10","21h2","w"] or ["10","21h2"]
    if (letter && parts.length === 3 && parts[2] === letter) {
      return e;
    }
    if (parts.length === 2) {
      generic = e;
    }
  }

  // Edition is null/undefined OR matching edition not found
  if (!letter) {
    if (generic) return generic;
    return candidates
      .filter(e => e.eolDate && e.eolDate !== 'False')
      .sort(
        (a, b) =>
          new Date(a.eolDate!).getTime() - new Date(b.eolDate!).getTime()
      )[0];
  }

  // No match for the letter → fallback to generic if exists
  return generic;
}

/**
 * Parse a semver string
 * @param v - e.g. "14.4.1" or "10.15"
 * @returns the parsed semver, or null if the string is not a valid semver
 */
function safeParseSemver(v: string): SemVer | null {
  // Accept "14.4.1" or "10.15" (coerce handles missing patch)
  const parsed = semver.coerce(v);
  return parsed && semver.valid(parsed) ? parsed : null;
}

/**
 * Get the EOL status for a given date
 * @param s - e.g. "2025-01-01"
 * @returns the EOL status, or "unknown" if the date is not a valid date
 */
export function eolStatus(s?: string | null): EOLStatusType {
  if (!s) return 'unknown';

  if (s === 'False') return 'supported';

  const inputDate = parseISO(s);
  const now = new Date();

  if (isBefore(inputDate, now)) {
    return 'danger';
  }

  const diffMonths = differenceInMonths(inputDate, now);

  if (diffMonths <= EOL_WARN_THRESHOLD_MONTHS) {
    return 'warning';
  }

  return 'supported';
}

/**
 * Get the EOL data for the given devices
 * @param assets - the assets to get the EOL data for
 * @param allEols - all EOLs for the devices
 * @returns the assets with the EOL data
 */
export function getEolDataForDevices(
  assets: Asset[],
  allEols: EndOfLife[]
): Asset[] {
  if (assets.length === 0 || allEols.length === 0) return assets;

  return assets.map(asset => {
    if (!asset.device?.osType || !asset.device?.reportingData) {
      return asset;
    }

    const eolData = checkDeviceEol(
      {
        osType: asset.device?.osType === 'darwin' ? 'macos' : 'windows',
        osVersion: asset.device?.reportingData?.osVersion,
        editionId: asset.device?.reportingData?.editionId,
      },
      allEols
    );

    const newAsset = {
      ...asset,
      device: {
        ...asset.device,
        reportingData: {
          ...asset.device?.reportingData,
          eolData: {
            status: eolStatus(eolData?.eolDate ?? null),
            eolDate: eolData?.eolDate ?? '',
          },
        },
      },
    };
    return newAsset;
  });
}

/**
 * Helper function to check if all devices have supported OS versions
 * @param osDistribution - The OS distribution data containing version counts and EOL status
 * @returns true if all devices have supported OS versions (or unknown), false otherwise
 */
export const areAllDevicesSupported = (
  osDistribution: VersionCounts
): boolean => {
  return Object.values(osDistribution)
    .flatMap(osVersions => Object.values(osVersions ?? {}))
    .every(
      versionInfo =>
        versionInfo.eolStatus === EOLStatusEnum.SUPPORTED ||
        versionInfo.eolStatus === EOLStatusEnum.UNKNOWN
    );
};
